import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, FileText, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Field, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { clientApi } from '@/config/axios';
import { InlineReferralReview } from '../InlineReferralReview';
import { RecordReview } from './RecordReview';
import type { RuntimeRun } from './runtimeContracts';

const actionable = new Set(['pending_review', 'needs_attention']);
const resolvedIntake = (run: RuntimeRun) => ['approved', 'rejected', 'superseded'].includes(run.intake_status ?? '');
const pendingAction = (run: RuntimeRun) =>
  run.actions.some((a) => actionable.has(a.status) && !(a.kind === 'referral_review' && resolvedIntake(run)));
export function latestCards(runs: RuntimeRun[]) {
  const seen = new Set<string>();
  const cards: RuntimeRun[] = [];
  for (const run of [...runs].sort((a, b) => b.created_at.localeCompare(a.created_at))) {
    const source = run.document_id ? `${run.document_id}:${run.workflow}:${run.revision_id}` : run.id;
    // Historical failed attempts without an outstanding action are replaced by
    // the newer preparation. Never hide a distinct action awaiting a decision.
    if (!seen.has(source) || pendingAction(run)) cards.push(run);
    seen.add(source);
  }
  return cards.sort((a, b) => Number(b.status === 'needs_attention') - Number(a.status === 'needs_attention'));
}
export function needsDecision(run: RuntimeRun) {
  return run.status === 'needs_attention' || pendingAction(run);
}
const explanations: Record<string, string> = {
  record_review_required:
    'Review the proposed history, imaging and referral statements below. Compare each with its source, then accept selected changes or defer with a note. Laboratory review is separate.',
  record_processing_interrupted:
    'Processing was interrupted. Review completed sections and retry incomplete sections below.',
  laboratory_extraction_incomplete:
    'Laboratory processing failed on some pages. This is not a request to confirm an unspecified clinical problem. Open the document for the page-specific processing reasons and Retry lab extraction. After processing completes, select and save reviewed lab results there. This task does not prepare the full medical history; accepting it would not populate History and referral records.',
  legacy_patient_changed:
    'The patient association changed after the original intake. Review the current document, then prepare a new draft.',
  legacy_source_needs_review:
    'The earlier intake needs review. Open the source and check its patient association. Prepare a new draft if the old intake no longer matches.',
  source_changed: 'This document changed after preparation. Review the current source before preparing a new draft.',
};
export function DecisionDeck({
  runs,
  base,
  org,
  manageNotes,
  onRefresh,
  onSource,
  renderReview,
  onFocusedRun,
}: {
  runs: RuntimeRun[];
  base: string;
  org: string;
  onFocusedRun?: (runId: string | null) => void;
  manageNotes: boolean;
  onRefresh: () => void;
  onSource: (source: { documentId: string; revisionId?: string; pageNumber?: number }) => void;
  renderReview: (id: string) => ReactNode;
}) {
  const [mode, setMode] = useState('attention');
  const [chosen, setChosen] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState<Record<string, { text: string; request: string }>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const all = latestCards(runs);
  const attention = all.filter(needsDecision);
  const cards = mode === 'attention' ? attention : all.filter((r) => !needsDecision(r));
  const index = Math.max(
    0,
    cards.findIndex((r) => r.id === chosen),
  );
  const run = cards[index];
  useEffect(() => {
    if (run && chosen !== run.id) setChosen(run.id);
  }, [run?.id, chosen]);
  useEffect(() => {
    onFocusedRun?.(run?.preparation_version === 'record-review.v1' ? run.id : null);
  }, [run?.id, run?.preparation_version, onFocusedRun]);
  const actions = run?.actions.filter((a) => !['superseded', 'expired', 'rejected'].includes(a.status)) ?? [];
  const note = run ? (notes[run.id]?.text ?? '') : '';
  const move = (next: number) => {
    setChosen(cards[next].id);
    setNotesOpen(false);
    setError('');
  };
  async function retry() {
    if (!run) return;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${base}/runs/${run.id}/retry`, {});
      onRefresh();
    } catch {
      setError(
        'Could not prepare this item. Check that its source is available and no execution is awaiting reconciliation.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function saveNote() {
    if (!run || !note.trim()) return;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${base}/runs/${run.id}/follow-up`, {
        request_id: notes[run.id].request,
        note: note.trim(),
      });
      setSaved((s) => ({ ...s, [run.id]: true }));
      setNotesOpen(false);
    } catch {
      setError(
        'The follow-up was not confirmed. Your note is kept here; retrying the same note will not create a duplicate. Check patient access if it continues to fail.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="flex flex-col gap-4" aria-label="Assistant decision cards">
      <Tabs
        value={mode}
        onValueChange={(value) => {
          setMode(value);
          setChosen(null);
          setNotesOpen(false);
          setError('');
        }}>
        <TabsList>
          <TabsTrigger value="attention">Needs attention ({attention.length})</TabsTrigger>
          <TabsTrigger value="other">Other items ({all.length - attention.length})</TabsTrigger>
        </TabsList>
      </Tabs>
      {run ? (
        <>
          <nav className="flex items-center justify-between gap-3" aria-label="Decision card navigation">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous card"
              disabled={index === 0 || busy}
              onClick={() => move(index - 1)}>
              <ArrowLeft />
            </Button>
            <p role="status">
              Card {index + 1} of {cards.length}
            </p>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next card"
              disabled={index === cards.length - 1 || busy}
              onClick={() => move(index + 1)}>
              <ArrowRight />
            </Button>
          </nav>
          <Card>
            <CardHeader>
              <Badge variant="secondary">
                {run.status === 'needs_attention'
                  ? 'Needs your attention'
                  : needsDecision(run)
                    ? 'Decision needed'
                    : run.status.replaceAll('_', ' ')}
              </Badge>
              <CardTitle>{run.document_name || run.workflow.replaceAll('_', ' ')}</CardTitle>
              <CardDescription>
                {run.patient_name ||
                  (run.patient_id ? 'Patient linked — verify in review' : 'Patient not yet identified')}{' '}
                · {new Date(run.created_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p>
                {run.reason
                  ? explanations[run.reason] || run.reason.replaceAll('_', ' ')
                  : needsDecision(run)
                    ? 'Review the proposed information below, confirm the source and patient, then accept the exact action.'
                    : 'No decision is currently requested. This item is available for reference.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {run.document_id && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      onSource({ documentId: run.document_id!, revisionId: run.revision_id ?? undefined })
                    }>
                    <FileText />
                    Open document
                  </Button>
                )}
                <Button variant="outline" onClick={() => setNotesOpen((v) => !v)}>
                  <StickyNote />
                  Notes
                </Button>
                {run.preparation_version !== 'record-review.v1' &&
                  ['needs_attention', 'cancelled', 'rejected'].includes(run.status) && (
                    <Button variant="outline" disabled={busy} onClick={() => void retry()}>
                      Prepare current document
                    </Button>
                  )}
              </div>
              {saved[run.id] && <p role="status">Follow-up saved to this patient’s checklist.</p>}
              {notesOpen &&
                (run.patient_id && manageNotes ? (
                  <div className="flex flex-col gap-3">
                    <Field>
                      <FieldLabel htmlFor="assistant-follow-up">
                        Follow-up for {run.patient_name || 'this patient'}
                      </FieldLabel>
                      <Textarea
                        id="assistant-follow-up"
                        value={note}
                        maxLength={4000}
                        disabled={busy}
                        placeholder="What needs to happen next?"
                        onChange={(event) => {
                          const text = event.target.value;
                          setNotes((n) => ({
                            ...n,
                            [run.id]: {
                              text,
                              request: n[run.id]?.text.trim() === text.trim() ? n[run.id].request : crypto.randomUUID(),
                            },
                          }));
                          setSaved((s) => ({ ...s, [run.id]: false }));
                        }}
                      />
                    </Field>
                    <p className="text-sm text-muted-foreground">
                      Saves an open patient checklist task linked to this document. It does not accept the proposed
                      action.
                    </p>
                    <Button disabled={busy || !note.trim() || saved[run.id]} onClick={() => void saveNote()}>
                      Save follow-up
                    </Button>
                  </div>
                ) : (
                  <p>
                    {!run.patient_id
                      ? 'Identify the patient in the review below before saving a patient follow-up.'
                      : 'Patient update access is required to save follow-up notes.'}
                  </p>
                ))}
              {run.preparation_version === 'record-review.v1' ? (
                <RecordReview key={run.id} base={base} runId={run.id} onSource={onSource} onChanged={onRefresh} />
              ) : run.legacy_job_id && (needsDecision(run) || actions.some((a) => a.kind === 'referral_review')) ? (
                <InlineReferralReview
                  key={run.legacy_job_id}
                  org={org}
                  jobId={run.legacy_job_id}
                  onOpenCurrentSource={run.document_id ? () => onSource({ documentId: run.document_id! }) : undefined}
                />
              ) : (
                actions
                  .filter((a) => actionable.has(a.status) || a.status === 'approved')
                  .map((a) => <div key={a.id}>{renderReview(a.id)}</div>)
              )}
              {error && <p role="alert">{error}</p>}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{mode === 'attention' ? 'No decisions waiting' : 'No other items'}</CardTitle>
            <CardDescription>
              {mode === 'attention'
                ? 'Items requiring a decision will appear here. Other items remain available in the second tab.'
                : 'Processed and in-progress items appear here.'}
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">
        Up to 100 tasks, with unresolved decisions prioritized. Repeated reference items are grouped.
      </p>
    </section>
  );
}
